import { Button, Flex, Form, Input, Select, Space, Switch } from 'antd';
import { FC } from 'react';
import { ArrowDown } from 'react-bootstrap-icons';

import { ButtonSuccess } from '@/common/components';
import { AppFormProps } from '@/common/interfaces';
import { defineInputStatus } from '@/common/utils';

import { DomainsAllAutocomplete, DomainsAllAutocompleteBySite, DomainVisitors } from '@/modules/Domains';
import {
  ExternalRedirectFormFields,
  ExternalRedirectFormInputs,
  ExternalRedirectTargetType,
} from '@/modules/ExternalRedirects';
import { PageNodesAllAutocomplete } from '@/modules/Pages';
import { REDIRECT_MATCH_MODE_OPTIONS, REDIRECT_STATUS_CODE_OPTIONS, RedirectEndpointEnum, RedirectEndpointType, SitesAllAutocomplete } from '@/modules/Sites';

interface Props extends AppFormProps<typeof ExternalRedirectFormInputs, ExternalRedirectFormFields> {
  siteId: string;
  targetSiteId?: string;
  handleChangeTargetSiteId: (siteId?: string) => void;
}

const endpointTypeOptions = [
  { value: RedirectEndpointEnum.Node, label: 'Сторінка (нода)' },
  { value: RedirectEndpointEnum.Path, label: 'Шлях' },
];

const targetTypeOptions = [
  { value: ExternalRedirectTargetType.SystemDomain, label: 'Домен у системі' },
  { value: ExternalRedirectTargetType.ExternalDomain, label: 'Зовнішній домен (не в системі)' },
  { value: ExternalRedirectTargetType.Site, label: 'Сайт (головний домен сайту)' },
];

export const ExternalRedirectsFormView: FC<Props> = ({
  formName,
  formType,
  form,
  initialValues,
  formErrors,
  isFormLoading,
  validationSchema,
  siteId,
  targetSiteId,
  handleChangeTargetSiteId,
  handleBack,
  handleFinish,
  handleFinishFailed
}) => {
  const sourceType = Form.useWatch(ExternalRedirectFormInputs.SourceType, form) ?? initialValues?.sourceType;
  const fromDomainId = (Form.useWatch(ExternalRedirectFormInputs.FromDomainId, form) ?? initialValues?.fromDomainId) as string | undefined;
  const toType = Form.useWatch(ExternalRedirectFormInputs.ToType, form) ?? initialValues?.toType;
  const targetType = Form.useWatch(ExternalRedirectFormInputs.TargetType, form) ?? initialValues?.targetType;
  const toSiteId = (Form.useWatch(ExternalRedirectFormInputs.ToSiteId, form) ?? initialValues?.toSiteId) as string | undefined;

  const isTargetNodeAllowed = toType !== ExternalRedirectTargetType.ExternalDomain;

  const effectiveTargetSiteId = toType === ExternalRedirectTargetType.Site ? String(toSiteId ?? '') : (targetSiteId ?? '');

  return (
    <Form
      form={ form }
      layout="vertical"
      name={ formName }
      initialValues={ initialValues }
      disabled={ isFormLoading }
      autoComplete="off"
      onFinish={ handleFinish }
      onFinishFailed={ handleFinishFailed }
    >
      <Form.Item hidden name={ ExternalRedirectFormInputs.SiteId }/>

      <Form.Item
        required
        label="Домен-джерело (з сайту)"
        name={ ExternalRedirectFormInputs.FromDomainId }
        dependencies={ [ExternalRedirectFormInputs.ToDomainId] }
        hasFeedback
        help={ formErrors?.fromDomainId }
        validateStatus={ defineInputStatus(isFormLoading, formErrors?.fromDomainId) }
        rules={ validationSchema.fromDomainId }
      >
        <DomainsAllAutocompleteBySite siteId={ siteId } allowClear/>
      </Form.Item>

      <Form.Item
        label="Джерело — тип"
        name={ ExternalRedirectFormInputs.SourceType }
        rules={ validationSchema.sourceType }
      >
        <Select options={ endpointTypeOptions }/>
      </Form.Item>

      { sourceType === RedirectEndpointEnum.Node ? (
        <Form.Item
          label="З (сторінка)"
          name={ ExternalRedirectFormInputs.SourceNodeId }
          hasFeedback
          help={ formErrors?.sourceNodeId }
          validateStatus={ defineInputStatus(isFormLoading, formErrors?.sourceNodeId) }
          rules={ validationSchema.sourceNodeId }
        >
          <PageNodesAllAutocomplete siteId={ siteId } allowClear/>
        </Form.Item>
      ) : (
        <Form.Item
          label="З (шлях)"
          name={ ExternalRedirectFormInputs.SourcePath }
          hasFeedback
          help={ formErrors?.sourcePath }
          validateStatus={ defineInputStatus(isFormLoading, formErrors?.sourcePath) }
          rules={ validationSchema.sourcePath }
          normalize={ (value) => value ? value.toLowerCase() : value }
        >
          <Input allowClear placeholder="/old-path"/>
        </Form.Item>
      ) }

      <Form.Item noStyle>
        <Flex align="center" justify="center">
          <ArrowDown size={ 30 }/>
        </Flex>
      </Form.Item>

      <Form.Item
        required
        label="Ціль — куди"
        name={ ExternalRedirectFormInputs.ToType }
        hasFeedback
        help={ formErrors?.toType }
        validateStatus={ defineInputStatus(isFormLoading, formErrors?.toType) }
        rules={ validationSchema.toType }
      >
        <Select options={ targetTypeOptions }/>
      </Form.Item>

      { toType === ExternalRedirectTargetType.SystemDomain && (
        <Form.Item
          label="Домен-ціль (у системі)"
          name={ ExternalRedirectFormInputs.ToDomainId }
          dependencies={ [ExternalRedirectFormInputs.FromDomainId] }
          hasFeedback
          help={ formErrors?.toDomainId }
          validateStatus={ defineInputStatus(isFormLoading, formErrors?.toDomainId) }
          rules={ validationSchema.toDomainId }
        >
          <DomainsAllAutocomplete allowClear excludeId={ fromDomainId }/>
        </Form.Item>
      ) }

      { toType === ExternalRedirectTargetType.ExternalDomain && (
        <Form.Item
          label="Зовнішній домен"
          name={ ExternalRedirectFormInputs.ToExternalDomain }
          hasFeedback
          help={ formErrors?.toExternalDomain }
          validateStatus={ defineInputStatus(isFormLoading, formErrors?.toExternalDomain) }
          rules={ validationSchema.toExternalDomain }
          normalize={ (value) => value ? value.toLowerCase() : value }
        >
          <Input allowClear placeholder="example.com"/>
        </Form.Item>
      ) }

      { toType === ExternalRedirectTargetType.Site && (
        <Form.Item
          label="Сайт-ціль (буде взято головний домен)"
          name={ ExternalRedirectFormInputs.ToSiteId }
          dependencies={ [ExternalRedirectFormInputs.SiteId] }
          hasFeedback
          help={ formErrors?.toSiteId }
          validateStatus={ defineInputStatus(isFormLoading, formErrors?.toSiteId) }
          rules={ validationSchema.toSiteId }
        >
          <SitesAllAutocomplete allowClear excludeId={ siteId }/>
        </Form.Item>
      ) }

      <Form.Item
        label="Ціль — тип"
        name={ ExternalRedirectFormInputs.TargetType }
        rules={ validationSchema.targetType }
      >
        <Select
          options={
            isTargetNodeAllowed
              ? endpointTypeOptions
              : endpointTypeOptions.filter(({ value }) => value !== RedirectEndpointEnum.Node)
          }
        />
      </Form.Item>

      { targetType === RedirectEndpointEnum.Node && isTargetNodeAllowed ? (
        <>
          { toType !== ExternalRedirectTargetType.Site && (
            <Form.Item label="Сайт цілі (для вибору сторінки)">
              <SitesAllAutocomplete
                allowClear
                value={ targetSiteId || undefined }
                onChange={ (value) => handleChangeTargetSiteId(value as string | undefined) }
              />
            </Form.Item>
          ) }

          <Form.Item
            label="До (сторінка)"
            name={ ExternalRedirectFormInputs.TargetNodeId }
            hasFeedback
            help={ formErrors?.targetNodeId }
            validateStatus={ defineInputStatus(isFormLoading, formErrors?.targetNodeId) }
            rules={ validationSchema.targetNodeId }
          >
            <PageNodesAllAutocomplete
              siteId={ effectiveTargetSiteId }
              allowClear
              disabled={ !effectiveTargetSiteId }
            />
          </Form.Item>
        </>
      ) : (
        <Form.Item
          label="До (шлях)"
          name={ ExternalRedirectFormInputs.TargetPath }
          hasFeedback
          help={ formErrors?.targetPath }
          validateStatus={ defineInputStatus(isFormLoading, formErrors?.targetPath) }
          rules={ validationSchema.targetPath }
          normalize={ (value) => value ? value.toLowerCase() : value }
        >
          <Input allowClear placeholder="/new-path"/>
        </Form.Item>
      ) }

      <Form.Item
        required
        label="Код редіректу"
        name={ ExternalRedirectFormInputs.StatusCode }
        hasFeedback
        help={ formErrors?.statusCode }
        validateStatus={ defineInputStatus(isFormLoading, formErrors?.statusCode) }
        rules={ validationSchema.statusCode }
      >
        <Select options={ REDIRECT_STATUS_CODE_OPTIONS }/>
      </Form.Item>

      <Form.Item
        required
        label="Режим співставлення"
        name={ ExternalRedirectFormInputs.MatchMode }
        tooltip="Точний збіг — лише точний шлях (з query). Вкладеність — шлях і вкладені на одну ціль. Wildcard-редірект — префіксне переписування /one/* → /two/*."
        rules={ validationSchema.matchMode }
      >
        <Select
          options={ REDIRECT_MATCH_MODE_OPTIONS.map(({ value, label, description }) => ({
            value,
            label,
            title: description,
          })) }
        />
      </Form.Item>

      <Form.Item
        required
        label="Для відвідувача"
        name={ ExternalRedirectFormInputs.ForVisitor }
        hasFeedback
        help={ formErrors?.forVisitor }
        validateStatus={ defineInputStatus(isFormLoading, formErrors?.forVisitor) }
        rules={ validationSchema.forVisitor }
      >
        <Select allowClear>
          <Select.Option value={ DomainVisitors.Any }>Будь-хто</Select.Option>
          <Select.Option value={ DomainVisitors.GoogleBot }>Google Bot</Select.Option>
          <Select.Option value={ DomainVisitors.Person }>Людина</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item
        label="Активний"
        layout="horizontal"
        name={ ExternalRedirectFormInputs.IsActive }
        hasFeedback
        help={ formErrors?.isActive }
        validateStatus={ defineInputStatus(isFormLoading, formErrors?.isActive) }
        rules={ validationSchema.isActive }
      >
        <Switch/>
      </Form.Item>

      <Form.Item wrapperCol={{ span: 24 }}>
        <Space align="center" style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button type="default" onClick={ handleBack }>
            Скасувати
          </Button>
          <ButtonSuccess loading={ isFormLoading } type="primary" htmlType="submit">
            { formType === 'edit' ? 'Зберегти' : 'Створити' }
          </ButtonSuccess>
        </Space>
      </Form.Item>
    </Form>
  );
};
